import { useEffect, useState } from "react";
import { Heading } from "../components/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/table";
import { collection, onSnapshot, query, Timestamp, doc, getDoc, writeBatch, setDoc, deleteDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import EmptyState from "./components/empty-state";
import { PlusIcon, ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, EyeSlashIcon, EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { formatDate, getNextMonday, getNextPaymentDate, getPreviousMonday, getWeekNumber } from "../helpers/date.helpers";
import { formatCurrency } from "../helpers/currency.helpers";
import { Input } from "../components/input";
import { Dropdown, DropdownButton, DropdownMenu, DropdownItem, DropdownDivider } from "../components/dropdown";
import { BSPayrollItem } from "@/models/payrollItem";
import { BSGroom } from "@/models/groom";
import { useTableSort } from '../hooks/useTableSort';
import { useColumnVisibility, ColumnConfig } from '../hooks/useColumnVisibility';
import { useTableSearch } from '../hooks/useTableSearch';
import { Switch, SwitchField } from "../components/switch";
import { Label } from "../components/fieldset";
import { Button } from "../components/button";
import { Alert, AlertActions, AlertDescription, AlertTitle } from "../components/alert";
import { BSParams } from "@/models/params";
import { Dialog, DialogTitle, DialogBody, DialogActions } from "../components/dialog";
import { Fieldset } from "../components/fieldset";
import { Select } from "../components/select";
import { Checkbox } from "../components/checkbox";
import { formatDateToInputString } from "../helpers/date.helpers";
import * as Headless from "@headlessui/react";

export default function Payroll() {
  // Data
  const [payrollItems, setPayrollItems] = useState<BSPayrollItem[]>([]);
  const [grooms, setGrooms] = useState<BSGroom[]>([]);
  const [hidePaid, setHidePaid] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastPayrollDate, setLastPayrollDate] = useState<Date | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const emptyPayrollItem: Omit<BSPayrollItem, 'id'> = {
    groomId: '',
    item: '',
    date: new Date(),
    paymentDate: new Date(),
    value: 0,
    paid: false
  };
  const [newPayrollItem, setNewPayrollItem] = useState(emptyPayrollItem);

  // Columns configuration
  const columns: ColumnConfig<BSPayrollItem>[] = [
    { field: 'date', label: 'Date', defaultVisible: true },
    { field: 'week', label: 'Week', defaultVisible: true },
    { field: 'groomId', label: 'Groom', defaultVisible: true },
    { field: 'item', label: 'Item', defaultVisible: true },
    { field: 'value', label: 'Value', defaultVisible: true },
    { field: 'paymentDate', label: 'Payment Date', defaultVisible: true },
    { field: 'paid', label: 'Paid', defaultVisible: true },
    { field: 'actions', label: 'Actions', defaultVisible: true },
  ];

  // Add hooks
  const { sortField, sortDirection, handleSort, getSortedData } = useTableSort<BSPayrollItem>('date');
  const { toggleColumn, isColumnVisible } = useColumnVisibility<BSPayrollItem>(columns);
  const { searchQuery, setSearchQuery, getFilteredData } = useTableSearch<BSPayrollItem>();

  // Fetch data
  useEffect(() => {
    const fetchPayrollItems = () => {
      const q = query(collection(db, "payroll"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSPayrollItem[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as BSPayrollItem;
          const date = data.date instanceof Timestamp
            ? data.date.toDate()
            : data.date;
          const paymentDate = data.paymentDate instanceof Timestamp
            ? data.paymentDate.toDate()
            : data.paymentDate;
          items.push({ ...data, id: doc.id, date, paymentDate });
        });
        setPayrollItems(items);
      });
    };

    const fetchGrooms = () => {
      const q = query(collection(db, "grooms"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSGroom[] = [];
        querySnapshot.forEach((doc) => {
          items.push({ ...doc.data() as BSGroom, id: doc.id });
        });
        setGrooms(items);
      });
    };

    fetchPayrollItems();
    fetchGrooms();
  }, []);

  // Add effect to fetch params
  useEffect(() => {
    const fetchParams = async () => {
      const paramsRef = doc(db, "params", "B1XduGleWvW0eXKqZ743");
      const paramsSnap = await getDoc(paramsRef);
      const params = paramsSnap.data() as BSParams;
      const lastPayroll = params.lastPayroll instanceof Timestamp
        ? params.lastPayroll.toDate()
        : params.lastPayroll;
      setLastPayrollDate(lastPayroll);
    };

    fetchParams();
  }, []);

  const generatePayrollItems = async () => {
    setIsGenerating(true);
    const batch = writeBatch(db);

    try {
      const currentDate = new Date();
      const activeGrooms = grooms.filter(groom => {
        const startDate = groom.startDate ? (groom.startDate instanceof Date ? groom.startDate : new Date(groom.startDate)) : null;
        const endDate = groom.endDate ? (groom.endDate instanceof Date ? groom.endDate : new Date(groom.endDate)) : null;
        
        // If no start date, only check end date
        if (!startDate) return !endDate || endDate >= currentDate;
        
        // If has start date, check both conditions
        return startDate <= currentDate && (!endDate || endDate >= currentDate);
      });

      if (!lastPayrollDate) throw new Error("Last payroll date not found");

      // For each active groom
      for (const groom of activeGrooms) {
        let currentWeekStart = getNextMonday(lastPayrollDate);
        const finalWeekStart = getPreviousMonday(new Date());

        // Generate items for each week
        while (currentWeekStart <= finalWeekStart) {
          const paymentDate = getNextPaymentDate(currentWeekStart);

          const payrollItem: Omit<BSPayrollItem, 'id'> = {
            groomId: groom.id,
            item: 'Weekly Salary',
            date: currentWeekStart,
            paymentDate,
            value: groom.weeklySalary,
            paid: false
          };

          const newPayrollRef = doc(collection(db, 'payroll'));
          batch.set(newPayrollRef, { ...payrollItem, id: newPayrollRef.id });

          // Move to next week
          currentWeekStart = new Date(currentWeekStart);
          currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        }
      }

      // Update the lastPayroll date in params
      const paramsRef = doc(db, "params", "B1XduGleWvW0eXKqZ743");
      batch.update(paramsRef, {
        lastPayroll: new Date()
      });

      await batch.commit();
      setLastPayrollDate(new Date());
      setIsConfirmDialogOpen(false);
    } catch (error) {
      console.error('Error generating payroll:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Add SortableHeader component
  const SortableHeader = ({ field, children }: { field: keyof BSPayrollItem, children: React.ReactNode }) => {
    const isCurrent = sortField === field;
    return (
      <TableHeader 
        className="cursor-pointer select-none group hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <span className="inline-flex">
            {isCurrent ? (
              sortDirection === 'asc' ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )
            ) : (
              <ChevronUpDownIcon className="w-4 h-4 opacity-0 group-hover:opacity-50" />
            )}
          </span>
        </div>
      </TableHeader>
    );
  };

  // Add filtered data function
  const getFilteredPayrollItems = () => {
    let filtered = payrollItems;
    
    // Filter out paid items if hidePaid is true
    if (hidePaid) {
      filtered = filtered.filter(item => !item.paid);
    }

    // Apply search filter
    filtered = getFilteredData(filtered, (item) => [
      formatDate(item.date) || '',
      grooms.find(g => g.id === item.groomId)?.name || '',
      item.item || '',
      formatCurrency(item.value),
      formatDate(item.paymentDate) || '',
      item.paid ? 'Yes' : 'No'
    ]);

    return getSortedData(filtered);
  };

  // Update save function to handle both new items and updates
  const savePayrollItem = async () => {
    try {
      if (newPayrollItem.groomId === '') {
        // TODO: Add validation error handling
        return;
      }

      if ('id' in newPayrollItem) {
        // Update existing item
        await updateDoc(doc(db, 'payroll', newPayrollItem.id), newPayrollItem);
      } else {
        // Create new item
        const newDocRef = doc(collection(db, 'payroll'));
        await setDoc(newDocRef, { ...newPayrollItem, id: newDocRef.id });
      }

      setIsDialogOpen(false);
      setNewPayrollItem(emptyPayrollItem);
    } catch (error) {
      console.error('Error saving payroll item:', error);
      // TODO: Add error handling
    }
  };

  // Add reset form function
  const resetForm = () => {
    setNewPayrollItem(emptyPayrollItem);
  };

  // Add edit and delete functions
  const editPayrollItem = (item: BSPayrollItem) => {
    setNewPayrollItem(item);
    setIsDialogOpen(true);
  };

  const deletePayrollItem = async (item: BSPayrollItem) => {
    await deleteDoc(doc(db, 'payroll', item.id));
  };

  // Add toggle paid function
  const togglePaid = async (item: BSPayrollItem) => {
    try {
      await updateDoc(doc(db, 'payroll', item.id), {
        paid: !item.paid
      });
    } catch (error) {
      console.error('Error toggling paid status:', error);
      // TODO: Add error handling
    }
  };

  // Rename and modify the function to handle both paid and unpaid states
  const toggleDatePaid = async (item: BSPayrollItem) => {
    try {
      const batch = writeBatch(db);
      
      // Find all items with the same day
      const itemsToUpdate = payrollItems.filter(payrollItem => 
        payrollItem.date.getFullYear() === item.date.getFullYear() &&
        payrollItem.date.getMonth() === item.date.getMonth() &&
        payrollItem.date.getDate() === item.date.getDate() &&
        payrollItem.paid === item.paid  // Only update items with same paid status
      );

      // Update each item in the batch
      itemsToUpdate.forEach(payrollItem => {
        const docRef = doc(db, 'payroll', payrollItem.id);
        batch.update(docRef, { paid: !item.paid });  // Toggle the paid status
      });

      await batch.commit();
    } catch (error) {
      console.error('Error toggling date paid status:', error);
    }
  };

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Payroll</Heading>
        <div className="flex items-center gap-4">
          <SwitchField>
            <Label>Showing {hidePaid ? 'pending payments' : 'all payments'}</Label>
            <Switch color="green" checked={hidePaid} onChange={setHidePaid} />
          </SwitchField>
          <div className="relative">
            <Input
              type="search"
              placeholder="Search payroll..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[250px]"
            />
          </div>
          <Dropdown>
            <DropdownButton>
              <EyeIcon className="w-5 h-5 mr-2" />
              Columns
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              {columns.map(({ field, label }) => (
                <DropdownItem
                  key={field}
                  onClick={() => toggleColumn(field)}
                >
                  <div className="flex items-center gap-2">
                    {isColumnVisible(field) ? (
                      <EyeIcon className="w-4 h-4" />
                    ) : (
                      <EyeSlashIcon className="w-4 h-4" />
                    )}
                    {label}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
          >
            Add Payroll Item
          </Button>
        </div>
      </div>

      {payrollItems.length > 0 ? (
        <>
          <Table
            dense
            striped
            className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)]"
          >
            <TableHead>
              <TableRow>
                {columns.map(({ field, label }) => 
                  isColumnVisible(field) && (
                    <SortableHeader key={field} field={field}>
                      {label}
                    </SortableHeader>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {getFilteredPayrollItems().map((item) => (
                <TableRow key={item.id}>
                  {isColumnVisible('date') && (
                    <TableCell>{formatDate(item.date)}</TableCell>
                  )}
                  {isColumnVisible('week') && (
                    <TableCell>{getWeekNumber(item.date)}</TableCell>
                  )}
                  {isColumnVisible('groomId') && (
                    <TableCell>
                      {grooms.find(g => g.id === item.groomId)?.name || 'Unknown'}
                    </TableCell>
                  )}
                  {isColumnVisible('item') && (
                    <TableCell>{item.item}</TableCell>
                  )}
                  {isColumnVisible('value') && (
                    <TableCell>{formatCurrency(item.value)}</TableCell>
                  )}
                  {isColumnVisible('paymentDate') && (
                    <TableCell>{formatDate(item.paymentDate)}</TableCell>
                  )}
                  {isColumnVisible('paid') && (
                    <TableCell>
                      <Checkbox
                        checked={item.paid}
                        onChange={() => togglePaid(item)}
                      />
                    </TableCell>
                  )}
                  {isColumnVisible('actions') && (
                    <TableCell>
                      <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                        <Dropdown>
                          <DropdownButton plain aria-label="More options">
                            <EllipsisHorizontalIcon />
                          </DropdownButton>
                          <DropdownMenu anchor="bottom end">
                            <DropdownItem onClick={() => editPayrollItem(item)}>
                              Edit
                            </DropdownItem>
                            <DropdownItem onClick={() => toggleDatePaid(item)}>
                              {item.paid ? 'Mark Date Unpaid' : 'Mark Date Paid'}
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem onClick={() => deletePayrollItem(item)}>
                              Delete
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex w-full flex-wrap items-center justify-between gap-4 border-t border-zinc-950/10 pt-6 dark:border-white/10">
            <div>
              <Button 
                onClick={() => setIsConfirmDialogOpen(true)}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Generating...
                  </>
                ) : (
                  'Add Payroll Items'
                )}
              </Button>
              {lastPayrollDate && (
                <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                  Last payroll run: {formatDate(lastPayrollDate)}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              {/* Any other footer actions */}
            </div>
          </div>
        </>
      ) : (
        <>
          <EmptyState
            title="No Payroll Items"
            description="No payroll items have been generated yet"
          >
            <PlusIcon className="w-10 h-10" />
          </EmptyState>
          <div className="flex w-full flex-wrap items-center justify-between gap-4 border-t border-zinc-950/10 pt-6 dark:border-white/10">
            <div>
              <Button 
                onClick={() => setIsConfirmDialogOpen(true)}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Generating...
                  </>
                ) : (
                  'Add Payroll Items'
                )}
              </Button>
              {lastPayrollDate && (
                <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                  Last payroll run: {formatDate(lastPayrollDate)}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Confirmation Dialog */}
      <Alert 
        open={isConfirmDialogOpen} 
        onClose={() => !isGenerating && setIsConfirmDialogOpen(false)}
      >
        <AlertTitle>Generate Payroll Items</AlertTitle>
        <AlertDescription>
          This will generate payroll items for all active grooms since {formatDate(lastPayrollDate)}. 
          Are you sure you want to continue?
        </AlertDescription>
        <AlertActions>
          <Button 
            plain 
            onClick={() => setIsConfirmDialogOpen(false)}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={generatePayrollItems}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Confirm'}
          </Button>
        </AlertActions>
      </Alert>

      {/* Add PayrollItem Dialog */}
      <Dialog open={isDialogOpen} onClose={setIsDialogOpen}>
        <DialogTitle>New Payroll Item</DialogTitle>
        <DialogBody>
          <Fieldset>
            <div
              data-slot="control"
              className="grid grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-3"
            >
              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Groom</Label>
                <Select
                  value={newPayrollItem.groomId}
                  onChange={(e) => setNewPayrollItem({ ...newPayrollItem, groomId: e.target.value })}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                >
                  <option value="">Select a groom...</option>
                  {grooms.map(groom => (
                    <option key={groom.id} value={groom.id}>
                      {groom.name}
                    </option>
                  ))}
                </Select>
              </Headless.Field>

              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Item</Label>
                <Input
                  type="text"
                  value={newPayrollItem.item}
                  onChange={(e) => setNewPayrollItem({ ...newPayrollItem, item: e.target.value })}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                />
              </Headless.Field>

              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Date</Label>
                <input
                  type="date"
                  value={formatDateToInputString(newPayrollItem.date)}
                  onChange={(e) => setNewPayrollItem({ 
                    ...newPayrollItem, 
                    date: new Date(`${e.target.value}T00:00:00`) 
                  })}
                />
              </Headless.Field>

              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Payment Date</Label>
                <input
                  type="date"
                  value={formatDateToInputString(newPayrollItem.paymentDate)}
                  onChange={(e) => setNewPayrollItem({ 
                    ...newPayrollItem, 
                    paymentDate: new Date(`${e.target.value}T00:00:00`) 
                  })}
                />
              </Headless.Field>

              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Value</Label>
                <Input
                  type="number"
                  value={newPayrollItem.value}
                  onChange={(e) => setNewPayrollItem({ 
                    ...newPayrollItem, 
                    value: parseFloat(e.target.value) 
                  })}
                  className="mt-3 sm:col-span-2 sm:mt-0"
                />
              </Headless.Field>

              <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                <Label>Paid</Label>
                <Checkbox
                  checked={newPayrollItem.paid}
                  onChange={(e) => setNewPayrollItem({ ...newPayrollItem, paid: e })}
                />
              </Headless.Field>
            </div>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={savePayrollItem}>
            {'id' in newPayrollItem ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
